<template>
  <div class="sender-address-prices-header">
    <div class="row w-100">
      <div class="col-6 my-auto">
        <div class="assignment-number mr-4 d-flex align-items-center">
          <router-link :to="customersArea" class="d-flex align-items-center mr-4">
            <a class="back-to position-absolute">
              <mdicon name="ChevronLeft" />
            </a>
          </router-link>
          <span class="ml-1 sender-address-title">
            Prices for sender address of
            <b>{{ customer?.name || `${customer?.contactName} ${customer?.contactLastName}` }}</b> customer
          </span>
          <div class="d-flex flex-column badges">
            <b-badge v-if="selectedSenderAddress?.hasCustomPrices" variant="success" class="ml-3">
              Sender prices
            </b-badge>
            <b-badge
              v-if="selectedSenderAddress?.hasCustomPriorities"
              variant="warning"
              class="ml-3"
              :class="{ 'mt-1': selectedSenderAddress?.hasCustomPrices }"
            >
              Sender carriers
            </b-badge>
          </div>
        </div>
      </div>
      <div class="col-6 pr-0">
        <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9 col-xl-auto ml-4 pr-0">
            <div class="d-flex align-items-center mb-0">
              <label class="mt-0 mb-0 mr-3 font-weight-bold"> Sender Address: </label>
              <CustomSelect
                :selected="selectedSenderAddress?.id"
                :options="senderAddresses"
                :selectable="(option: Address) => option"
                placeholder="Choose sender address"
                @selected="changeSenderAddress"
              >
                <template #custom_option="{ option }: { option: Address }">
                  <div :class="'item-' + option.id">
                    <span class="status" />
                    <span class="name">{{ option.companyName }},</span>
                    {{ option.countryCode }}, {{ option.city }}, {{ option.addressLine }} {{ option.addressLine2 }}
                    <b-badge v-if="option.isDefault" class="ml-1" variant="success"> Default Address </b-badge>
                    <b-badge v-if="option.hasCustomPrices" variant="success" class="ml-1"> Sender prices </b-badge>
                  </div>
                </template>
              </CustomSelect>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { BasePrice } from '@/types/Models/BasePrice'
import type { CompanyWithLabel } from '@/types/Models/Company'
import type { Address } from '@/types/Models/Customer'
import { BBadge } from 'bootstrap-vue-next'
import { computed, type ComputedRef, defineComponent, type Ref, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { DEFAULT_LOADER } from '@/const/LoadersNames'
import { none } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useCustomersStore } from '@/store/customers'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import { usePricesStore } from '@/store/prices'
import { useUiStore } from '@/store/ui'
import { STAFF_CUSTOMERS_LIST } from '@/types/router'
import CustomSelect from '@/views/Components/Elements/Form/CustomSelect.vue'

export default defineComponent({
  name: 'SenderAddressPricesActionMenu',
  components: { BBadge, CustomSelect },
  setup() {
    const uiStore = useUiStore()
    const pricesStore = usePricesStore()
    const customersStore = useCustomersStore()
    const senderAddressesStore = useCustomersSenderAddressesStore()
    const route = useRoute()
    const customer: Ref<CompanyWithLabel | null> = ref(null)
    const selectedSenderAddress: Ref<Address | null> = ref(null)
    const defaultPrices: Ref<BasePrice[]> = ref([])

    const senderAddresses: ComputedRef<Address[]> = computed(() => senderAddressesStore.senderAddresses)
    const customers: ComputedRef<CompanyWithLabel[]> = computed(() => customersStore.allCustomers)
    const changedPrices: ComputedRef<BasePrice[]> = computed(() => pricesStore.changedPrices)
    const customersArea: RouterLink = { name: STAFF_CUSTOMERS_LIST }

    const changeSenderAddress = (val: string) => {
      selectedSenderAddress.value = senderAddresses.value.find((el: Address) => el.id === val) || null

      const companyId = customer.value?.id
      const addressId = selectedSenderAddress.value?.id
      if (companyId && addressId) {
        senderAddressesStore
          .selectSenderAddress({
            companyId,
            addressId,
          })
          .catch(none)
      }

      if (addressId) {
        void getPrices(addressId)
      }
    }

    const getPrices = async (id?: string) => {
      uiStore.loaderShow({ name: DEFAULT_LOADER })

      try {
        defaultPrices.value = await pricesStore.fetchDefault(id ?? '')
        const result = await pricesStore.fetch(id ?? '')

        if (result.length === 0) {
          pricesStore.setPrices(defaultPrices.value)
        }
      } catch (error) {
        none()
      } finally {
        uiStore.loaderHide({ name: DEFAULT_LOADER })
      }
    }

    watch(senderAddresses, (val) => {
      if (val.length) {
        selectedSenderAddress.value = { ...val[0] } as Address
        senderAddressesStore
          .selectSenderAddress({
            companyId: route.params.id as string,
            addressId: selectedSenderAddress.value.id,
          })
          .catch(none)
        void getPrices(selectedSenderAddress.value.id)
      }
    })

    watch(customers, (val) => {
      if (val.length) {
        customer.value = customers.value.find((el: CompanyWithLabel) => el.id === route.params.id) || null
      }
    })

    onBeforeRouteLeave((_to, _from) => {
      if (changedPrices.value && changedPrices.value.length > 0) {
        return new Promise((resolve) => {
          openModal('confirmWrap', {
            title: 'There are unsaved changes',
            message: 'Are you sure you want to cancel?',
            onConfirm: () => {
              pricesStore.reset().catch(none)
              resolve(true)
            },
            onCancel: () => {
              resolve(false)
            },
          })
        })
      }
      return true
    })

    return {
      customer,
      selectedSenderAddress,
      senderAddresses,
      customersArea,
      changeSenderAddress,
      getPrices,
    }
  },
})
</script>
